import type { PeriodGranularity } from '@ooh/contracts/period-granularity';
import type { FilterPanelLabels, RangePillLabels } from '@ooh/ui/components/filter-panel';
import type { PaginationLabels } from '@ooh/ui/components/pagination';

/**
 * Props-only display types for the client-offer-review proposal screen
 * (redesign v2 of the client-facing offer decision flow, see
 * `client-offer-review-header.tsx` and siblings). These are UI-shaped view
 * models, not `@ooh/contracts` DTOs — the real screen's route/data layer is
 * responsible for composing them from `OfferUnitListItem`
 * (`@ooh/contracts`'s `offer-unit.ts`), pricing (`offer-pricing.ts`), and
 * per-unit decision/availability state (`offer-version-unit-decisions.ts`,
 * `offer-availability.ts`) — deliberately not attempted 1:1 here since this
 * screen is still an interaction proposal, not a wired implementation.
 */

export type ClientOfferReviewDecision = 'accepted' | 'rejected' | null;

/**
 * Simplified 3-state booking status for a single period cell. The real
 * per-window reservation model (`UnitDecisionReservationStateSchema`:
 * NONE/SOFT_RESERVATION/OPTION/SOLD) is richer; this proposal collapses it
 * to what the client-facing view needs to render (free vs. taken vs. sold).
 */
export type ClientOfferReviewHalfStatus = 'free' | 'reserved' | 'sold';

/** Aligned to `@ooh/contracts`'s `PeriodGranularity` enum values. */
export type ClientOfferReviewGranularity = PeriodGranularity;

export type ClientOfferReviewFilterKey = 'ALL' | 'ACCEPTED' | 'REJECTED' | 'PENDING';
export type ClientOfferReviewPhotoMode = 'large' | 'small' | 'none';
export type ClientOfferReviewSortKey = 'city' | 'price' | 'decision';
export type ClientOfferReviewAccessMode = 'Link publiczny' | 'Zalogowany (SSO)';
/**
 * Controls the list/map two-pane split ratio
 * (`client-offer-review.mockup.tsx`'s `data-co-main` grid). Stepped via the
 * two chevron buttons in the map panel's top-left corner —
 * `wideList` = 85/15, `balanced` = 50/50, `wideMap` = 15/85.
 */
export type ClientOfferReviewLayoutStep = 'wideList' | 'balanced' | 'wideMap';

/**
 * "Results per page" for both the toolbar's page-size control and the
 * numbered pagination bar at the bottom of the list — selecting a preset
 * (20/50/100/200) or typing a custom count changes how many rows each page
 * holds. `'all'` puts every currently filtered row on a single page.
 * Custom counts are a plain `number`, so this stays `number | 'all'` rather
 * than a literal union of the four presets.
 */
export type ClientOfferReviewPageSize = number | 'all';

export interface ClientOfferReviewUnit {
  id: string;
  grw: string;
  /** `null` on both client-facing routes; populated only when `showSupplierIdentity` is true
   * (internal version-preview screen) AND the server actually supplied the value — see OQ-5/Decision 6. */
  supplierNumber: string | null;
  /** See `supplierNumber`. */
  supplier: string | null;
  address: string;
  city: string;
  /** `null` when the snapshot (and any live fallback) carries no voivodeship for this unit. */
  voivodeship: string | null;
  format: string;
  formatKey: string;
  dimensions: string;
  widthM: number;
  heightM: number;
  price: number | null;
  /** `null` when the unit has no geocoded location — never coerced to 0 (see CU-86cay892c). */
  lat: number | null;
  /** `null` when the unit has no geocoded location — see `lat`. */
  lng: number | null;
  /**
   * Client-visible free-text location description, live-resolved from
   * media_units.location_description (same source as the internal detail
   * screen's `DetailLocationCard`). Optional: absent on the mockup's
   * hand-authored fixture data and any caller that hasn't wired it through,
   * mirroring `mediaUnitId`'s precedent — the modal renders nothing when
   * absent or empty.
   */
  locationDescription?: string | null;
  sold: boolean;
  /**
   * False when this unit has zero pickable (free) periods — e.g. base status
   * ASK_AVAILABILITY with no free windows. `undefined`/`true` means the unit can be
   * accepted. When false, the accept action renders disabled with
   * `ClientOfferReviewUnitCardLabels.acceptDisabledHint` as its tooltip; reject is
   * unaffected. Bug fix: silent accept failure for units with zero pickable periods.
   */
  canAccept?: boolean;
  /** Per-half booking status, aligned index-for-index with the active granularity's underlying halves. */
  halves: ClientOfferReviewHalfStatus[];
  /** Thumb rendition (480px) for list cards, thumbnail strips, and the expanded-panel photo
   * block — `null` for formats with no photo asset, which falls back to the placeholder icon. */
  thumbnailUrl: string | null;
  /** Display rendition (1600px) for the detail modal and lightbox — `null` for formats with
   * no photo asset, which falls back to the placeholder icon. */
  displayUrl: string | null;
  /**
   * The real media-unit id (not `id`, the snapshot row id) — needed only to
   * build the detail modal's "view full details" deep link to
   * `/offers/:offerId/units/:unitId`. Optional: absent on the mockup's
   * hand-authored fixture data and on routes that don't wire it (public
   * link, internal version-preview), which is also exactly why that link
   * only ever renders on the authenticated SSO route.
   */
  mediaUnitId?: number;
  /**
   * True when this unit's accepted state is carried forward from a prior version with
   * unchanged terms AND the unit currently has an active SOLD reservation for this offer,
   * making it binding — offer-decision-carry-forward / Locked carry-forward entries are
   * enforced on finalize. The accept/reject controls and the period picker render disabled
   * with an explanatory hint; the client-side guard is a usability affordance only, the
   * server enforces the same constraint on finalize. `undefined`/`false` means the unit is
   * freely (re)decidable — including a carried-forward unit with no reservation, or only a
   * SOFT_RESERVATION/HARD_RESERVATION/OPTION, which still starts pre-selected as accepted.
   */
  locked?: boolean;
}

export interface ClientOfferReviewPeriod {
  id: string;
  label: string;
  /** Present for day granularity only — "{day} {month}" tooltip prefix. */
  title?: string;
  /** Indices into `ClientOfferReviewUnit.halves` this period is derived from. */
  hix: number[];
  minWidth: string;
  /** ISO date (YYYY-MM-DD) — the axis period's own date range, used to re-bucket
   * each unit's server AvailabilityPeriodCell[] onto this period by overlap
   * (build-review-periods.ts) and to merge selected periods into submission
   * ranges (merge-selected-periods.ts) without a raw-cell lookup. */
  start: string;
  end: string;
}

export interface ClientOfferReviewDiffItem {
  type: 'price' | 'removed' | 'added';
  label: string;
  detail: string;
}

/**
 * Bundled translated-string props for the client-offer-review domain
 * components, following the `MediaUnitPopupCardLabels`
 * (`packages/ui/src/components/media-unit-popup-card.tsx`) convention: one
 * `labels` prop object per component instead of dozens of flat string
 * props. The mockup passes literal Polish strings; the real route will pass
 * `t('...')` results — components themselves stay translation-agnostic.
 */
export interface ClientOfferReviewInvalidLinkLabels {
  title: string;
  description: string;
}

export interface ClientOfferReviewHeaderLabels {
  brandName: string;
  loggedInAs: string;
  versionPrefix: string;
}

export interface ClientOfferReviewStatusBannerLabels {
  diffTitle: (prevVersion: number) => string;
  diffCollapse: string;
  diffShow: (count: number) => string;
  diffTagPrice: string;
  diffTagRemoved: string;
  diffTagAdded: string;
}

export interface ClientOfferReviewSummaryStatsLabels {
  unitsLabel: string;
  citiesUnit: string;
  campaignLabel: string;
  totalNetLabel: string;
  accepted: string;
  rejected: string;
  pending: string;
  pendingHint: string;
  acceptedValueLabel: string;
  breakdownSummary: (
    accCount: number,
    pctAcc: number,
    rejCount: number,
    pctRej: number,
    pendCount: number,
    pctPend: number,
  ) => string;
  availableUnitsSuffix: (availCount: number) => string;
}

/** Labels for `ClientOfferReviewPageSizeControl` — split out from `ClientOfferReviewToolbarLabels`
 * so that component can take a narrow, self-contained props type instead of the full toolbar bag. */
export interface ClientOfferReviewPageSizeLabels {
  /** Full accessible name (screen-reader only). */
  pageSizeAriaLabel: string;
  /** Short visible caption rendered next to the trigger. */
  pageSizeLabel: string;
  pageSizeAllOption: string;
  /** Accessible name for the custom count input. */
  pageSizeCustomAriaLabel: string;
  pageSizeCustomPlaceholder: string;
}

export interface ClientOfferReviewToolbarLabels extends ClientOfferReviewPageSizeLabels {
  searchPlaceholder: string;
  filterAll: string;
  filterAccepted: string;
  filterRejected: string;
  filterPending: string;
  /** Full accessible name (screen-reader only). */
  photoModeAriaLabel: string;
  /** Short visible caption rendered next to the trigger — the control has no self-describing
   * text otherwise ("Małe" alone doesn't say what it's the size of). */
  photoModeLabel: string;
  photoModeLarge: string;
  photoModeSmall: string;
  photoModeNone: string;
  /** Full accessible name (screen-reader only). */
  sortAriaLabel: string;
  /** Short visible caption rendered next to the trigger. */
  sortLabel: string;
  sortByCity: string;
  sortByPrice: string;
  sortByDecision: string;
  /** Full accessible name (screen-reader only). */
  granularityAriaLabel: string;
  /** Short visible caption rendered next to the trigger. */
  granularityLabel: string;
  granularityHalfMonth: string;
  granularityMonth: string;
  granularityWeek: string;
  granularityDay: string;
  selectAllAriaLabel: string;
  selectAllHint: string;
  clearAllAriaLabel: string;
  clearAllHint: string;
}

/**
 * One row of the footer bar's custom-periods popover. Identified by `grw`
 * because that is the only reference id the unit card shows (see
 * `ClientOfferReviewUnitCardLabels.grwNumberLabel`) — showing the supplier
 * number here instead would leave the client unable to match a row back to a
 * card.
 */
export interface ClientOfferReviewCustomPeriodItem {
  id: string;
  grw: string;
  address: string;
  city: string;
  /** Pre-collapsed range, built by `summarizeSelectedPeriods`. */
  periodSummary: string;
  selectedCount: number;
  totalCount: number;
}

/**
 * Labels for the `FilterPanel` (`@ooh/ui/components/filter-panel`) row added
 * to the toolbar — one label per filterable field (see
 * `buildClientOfferReviewFilterFields` in `client-offer-review-filter-fields.ts`
 * for the current field set) plus the two translation-free label bags the
 * shared component itself requires
 * (`panel` for FilterPanel's own copy, `range` for the price RangePill's
 * Min/Max/Apply/Clear copy). Mirrors the `filterPanelLabels`/`rangeLabels`
 * split used by `apps/web/src/features/media-units/hooks/use-filter-labels.ts`.
 */
export interface ClientOfferReviewFilterPanelLabels {
  city: string;
  format: string;
  price: string;
  priceUnit: string;
  voivodeship: string;
  supplier: string;
  width: string;
  height: string;
  dimensionUnit: string;
  panel: FilterPanelLabels;
  range: RangePillLabels;
}

export interface ClientOfferReviewFooterBarLabels {
  disclaimer: string;
  summary: (accCount: number, rejCount: number, pendCount: number, accValueLabel: string) => string;
  accept: (versionNumber: number) => string;
  /** Count-free chip text — the count renders as a separate badge, which keeps
   * the visible label out of Polish plural agreement entirely. */
  customPeriodsChip: string;
  /** Full pluralised sentence, used as the chip's accessible name. */
  customPeriodsCount: (count: number) => string;
  /** Heading inside the custom-periods popover, above the per-unit breakdown. */
  customPeriodsTitle: string;
  /** Irreversibility warning shown next to `restoreCustomPeriods` inside the popover. */
  customPeriodsRestoreHint: string;
  /** Bulk action resetting every unit's period selection back to its default range. */
  restoreCustomPeriods: string;
  /** Accept button's disabled-hint tooltip/title when accCount is 0 — distinct from
   * the readOnly/preview `roHint`, which wins when both apply. */
  acceptDisabledNoAcceptedHint: string;
}

export interface ClientOfferReviewPhotoBlockLabels {
  zoomHint: string;
  soldLabel: string;
  placeholderCaptionLarge: string;
  placeholderCaptionSmall: string;
}

export interface ClientOfferReviewPeriodChipLabels {
  free: string;
  reserved: string;
  sold: string;
  selected: string;
  /** Screen-reader-only summary of the unit's currently selected range — the chip
   * strip is `aria-hidden` only while non-interactive (undecided/locked/read-only). */
  selectedRangeSummary: (selectedCount: number, totalCount: number) => string;
  /** `role="switch"` accessible name for a pickable free chip. */
  toggleAriaLabel: (periodLabel: string) => string;
  /** Appended to a pickable chip's title/aria-label when the unit is not yet accepted —
   * conveys that clicking this chip also accepts the whole nośnik, before the click. */
  willAcceptHint: string;
}

export interface ClientOfferReviewUnitCardLabels {
  soldLabel: string;
  acceptedBadge: string;
  rejectedBadge: string;
  priceConfirmationPending: string;
  netSuffix: string;
  locateAction: string;
  acceptAction: string;
  undoAcceptAction: string;
  /** Tooltip shown on the disabled accept action when a unit's `canAccept` is false. */
  acceptDisabledHint: string;
  rejectAction: string;
  undoRejectAction: string;
  /** Tooltip/inline-note shown on the disabled accept/reject controls and the disabled
   * period picker when the unit is `locked` (carried forward, binding) — states that the
   * choice was already agreed and a change requires the salesperson. */
  lockedHint: string;
  seeDetails: string;
  /** "Nr" prefix before the GRW code — the only reference id shown on the card now (supplier number/name/voivodeship dropped from this line; voivodeship already shows inline in the address). */
  grwNumberLabel: string;
  /** "Cena po rabacie" — the single agreed net price row shown to the client (reuses `unit.price`). No catalog/pre-discount price is ever rendered. */
  discountedPriceLabel: string;
  periodChips: ClientOfferReviewPeriodChipLabels;
  photoBlock: ClientOfferReviewPhotoBlockLabels;
}

export interface ClientOfferReviewUnitListLabels {
  noResultsTitle: string;
  clearFilters: string;
  unitCard: ClientOfferReviewUnitCardLabels;
  pagination: PaginationLabels;
}

export interface ClientOfferReviewConfirmationModalsLabels {
  selectAllTitle: string;
  selectAllBody: (availCount: number) => string;
  selectAllCancel: string;
  selectAllConfirm: string;
  clearAllTitle: string;
  clearAllBody: (accCount: number, rejCount: number) => string;
  clearAllCancel: string;
  clearAllConfirm: string;
  acceptOfferTitle: (versionNumber: number) => string;
  acceptOfferBody: (
    accCount: number,
    rejCount: number,
    pendCount: number,
    accValueLabel: string,
  ) => string;
  acceptOfferCancel: string;
  acceptOfferConfirm: string;
}

export interface ClientOfferReviewLightboxLabels {
  close: string;
  previous: string;
  next: string;
  frames: [string, string, string];
}

export interface ClientOfferReviewMapPanelLabels {
  fullscreenToggle: string;
  /** Left chevron (top-left cluster) — steps toward 85/15 (wider list, narrower map). */
  growListWidth: string;
  /** Right chevron (top-left cluster) — steps toward 15/85 (wider map, narrower list). */
  growMapWidth: string;
  legendAccepted: string;
  legendRejected: string;
  legendDigital: string;
  legendOther: string;
  legendSold: string;
}

/**
 * Status-to-color classes for a period's selected/free/reserved/sold state.
 * Shared between the card's read-only chip strip (client-offer-review-period-chips.tsx)
 * and the detail modal's interactive picker (client-offer-review-detail-period-picker.tsx)
 * so the two surfaces can never drift out of sync.
 */
export const CHIP_CLASS: Record<'selected' | ClientOfferReviewHalfStatus, string> = {
  selected: 'bg-status-free/15 border-status-free/60 text-status-free',
  reserved: 'bg-status-option/15 border-status-option/50 text-status-option',
  sold: 'bg-status-sold/15 border-status-sold/40 text-status-sold line-through',
  free: 'bg-background border-border text-muted-foreground',
};

/** Worst-case status among a period's underlying half-slots. */
export function statusForPeriod(
  unit: ClientOfferReviewUnit,
  period: ClientOfferReviewPeriod,
): ClientOfferReviewHalfStatus {
  let worst: ClientOfferReviewHalfStatus = 'free';
  for (const ix of period.hix) {
    const s = unit.halves[ix];
    if (s === 'sold') return 'sold';
    if (s === 'reserved') worst = 'reserved';
  }
  return worst;
}
